import {
  AppWindow,
  Bell,
  Blocks,
  FolderGit2,
  Gavel,
  Home,
  Layers,
  Library,
  Lock,
  type LucideIcon,
  Map as MapIcon,
  MessagesSquare,
  PlayCircle,
  Search,
  Settings,
  ShieldCheck,
  SquarePlus,
  Wrench,
  Zap,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { NeedsYouNavBadge } from '@vivicta/backstage-plugin-application-portal';

export type NavEntry = {
  to: string;
  title: string;
  icon: ReactNode;
  /** Shown after the title in the sidebar only, e.g. a count. */
  badge?: ReactNode;
};

/**
 * Shared between `VivictaSidebar` (renders these as links) and
 * `CommandPalette` (renders them as the "Pages" command group) — extracted
 * here so neither module has to import the other.
 */
export const navIcon = (Icon: LucideIcon) => (
  <Icon aria-hidden="true" className="size-4 shrink-0" />
);

/** Your applications first, then the work you run on them. */
export const WORKSPACE_ENTRIES: NavEntry[] = [
  {
    to: '/applications',
    title: 'Applications',
    icon: navIcon(AppWindow),
    badge: <NeedsYouNavBadge />,
  },
  {
    to: '/onboarding',
    title: 'New Application',
    icon: navIcon(SquarePlus),
  },
  { to: '/operations', title: 'Operations', icon: navIcon(Wrench) },
  { to: '/create', title: 'Actions', icon: navIcon(Zap) },
  { to: '/agent-status', title: 'Agent Jobs', icon: navIcon(PlayCircle) },
];

/** Browsing what exists; the sidebar mixes these with the stock plugins' pages. */
export const EXPLORE_ENTRIES: NavEntry[] = [
  { to: '/catalog', title: 'Catalog', icon: navIcon(FolderGit2) },
  { to: '/repository-map', title: 'Repository Map', icon: navIcon(MapIcon) },
];

export const GOVERNANCE_ENTRIES: NavEntry[] = [
  { to: '/compliance', title: 'Compliance', icon: navIcon(ShieldCheck) },
  { to: '/security', title: 'Security', icon: navIcon(Lock) },
  { to: '/policy-review', title: 'Policy Review', icon: navIcon(Gavel) },
  { to: '/policy-library', title: 'Policy Library', icon: navIcon(Library) },
  {
    to: '/compliance-chat',
    title: 'Compliance Chat',
    icon: navIcon(MessagesSquare),
  },
];

/**
 * Sidebar footer. Notifications and Settings deliberately live only in the top
 * bar (bell with its unread badge, avatar menu) — having them here as well
 * gave every account surface two competing entry points.
 */
export const FOOTER_ENTRIES: NavEntry[] = [
  { to: '/visualizer', title: 'Portal Anatomy', icon: navIcon(Blocks) },
];

export const HOME_ENTRY: NavEntry = {
  to: '/',
  title: 'Home',
  icon: navIcon(Home),
};

export const SEARCH_ENTRY: NavEntry = {
  to: '/search',
  title: 'Search',
  icon: navIcon(Search),
};

/** Reachable from the top bar rather than the sidebar, but still worth jumping to. */
const ACCOUNT_ENTRIES: NavEntry[] = [
  { to: '/notifications', title: 'Notifications', icon: navIcon(Bell) },
  { to: '/settings', title: 'Settings', icon: navIcon(Settings) },
];

/**
 * The sidebar's "Admin" section is built from the extension tree
 * (`navItems.take(...)`), not from this list — these are here so the command
 * palette can reach them and so breadcrumbs read "Unprocessed Entities"
 * rather than the path-humanized "Catalog Unprocessed Entities".
 */
const ADMIN_ENTRIES: NavEntry[] = [
  { to: '/devtools', title: 'DevTools', icon: navIcon(Wrench) },
  {
    to: '/catalog-unprocessed-entities',
    title: 'Unprocessed Entities',
    icon: navIcon(Layers),
  },
];

/** Every statically-known portal route — the `CommandPalette`'s "Pages" group. */
export const ALL_NAV_ENTRIES: NavEntry[] = [
  HOME_ENTRY,
  SEARCH_ENTRY,
  ...WORKSPACE_ENTRIES,
  ...EXPLORE_ENTRIES,
  ...GOVERNANCE_ENTRIES,
  ...FOOTER_ENTRIES,
  ...ACCOUNT_ENTRIES,
  ...ADMIN_ENTRIES,
];
